import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import App, {AppContext} from 'next/app';
import {useEffect, useRef} from 'react';
import {I18nextProvider} from 'react-i18next';
import {useDispatch, useStore} from 'react-redux';
import {checkAndRefreshAccessToken} from '../src/auth/common';
import useAuthResult, {setAuthResult} from '../src/auth/hook/useAuthResult';
import {actions as authActions} from '../src/auth/state/action';
import ErrorBoundary from '../src/common/component/ErrorBoundary';
import Meta from '../src/common/component/Meta';
import PAlert from '../src/common/component/PAlert';
import StructuredData from '../src/common/component/StructuredData';
import ThemeProvider from '../src/common/component/ThemeProvider';
import {
  IS_CLIENT,
  WEB_HOST,
  IS_DEBUG,
  getBgCN,
  IS_PROD,
} from '../src/common/constant';
import {Page} from '../src/common/constant/page';
import Layout from '../src/common/container/Layout';
import PToast from '../src/common/container/PToast';
import {
  IPageAppContext,
  IPageContext,
  LiteVersionContext,
  PageAppContext,
  PageContext,
} from '../src/common/context';
import {waitSaga} from '../src/common/function';
import useTransitionManager from '../src/common/hook/transition/useTransitionManager';
import useDeepMemoized from '../src/common/hook/useDeepMemoized';
import useFetchWebToAppBanner from '../src/common/hook/useFetchWebToAppBanner';
import useIsomorphicLayoutEffect from '../src/common/hook/useIsomorphicLayoutEffect';
import useResizeCssVars from '../src/common/hook/useResizeCssVars';
import useUserAgent from '../src/common/hook/useUserAgent';
import {getI18nextInstance} from '../src/common/i18n';
import {actions as commonActions} from '../src/common/state/action';
import {IKwApp, ILanguage, ITheme} from '../src/common/type';
import {pushLog, checkAndRouteToDebugLog} from '../src/common/util/debug';
import {
  processBeforePopState,
  getCurrentClientPage,
} from '../src/common/util/PageRouter';
import {
  checkAndSendPageView,
  initTracking,
  initSentry,
} from '../src/common/util/tracking';
import {initGtm} from '../src/common/util/tracking/gtm';
import {getUserAgent} from '../src/common/util/userAgent';
import withRedux from '../src/common/withRedux';
import {createKakaowebtoonWeb} from '../src/commonSub/function';
import useIsLite from '../src/commonSub/hook/useIsLite';
import useKakaoSdk from '../src/commonSub/hook/useKakaoSdk';
import useRouteToContentObserver, {
  contentAdultMap,
  initializeContentAdultMap,
} from '../src/commonSub/hook/useRouteToContentObserver';
import 'swiper/swiper.scss';
import '../src/common/scss/reset.scss';
import '../src/common/scss/base.scss';
import '../src/common/scss/moment.scss';
import '../src/common/component/PageSwiper.scss';
import '../src/common/tailwind/tailwind.scss';

initSentry();

const KwApp: IKwApp = ({
  Component,
  router,
  pageProps,
  context,
  lang,
  serverContentAdultMap,
  theme,
}) => {
  const store = useStore();
  const dispatch = useDispatch();

  useIsomorphicLayoutEffect(() => {
    if (serverContentAdultMap) {
      for (const id in serverContentAdultMap) {
        if (id) {
          contentAdultMap[id] = serverContentAdultMap[id];
        }
      }
    }
  }, [serverContentAdultMap]);

  const {pageMap, prevPage, currentPage} = useTransitionManager({
    Component,
    pageProps,
    page: context.page,
  });
  // TODO: prevPage, page 에서 home 의 모든 페이지는 Main 을 가리킨다. 출시 직전까지  home 의 subPage 를 구분할 필요가 없다면 주석을 지우자
  const memoizedContext = useDeepMemoized({
    ...context,
    prevPage,
    page: currentPage,
  });

  const timeoutIdRef = useRef<number>();
  useEffect(() => {
    router.events.on('routeChangeStart', () => {
      checkAndRefreshAccessToken();
      dispatch(commonActions.setValue('isPageLoading', true));

      const timeoutId = timeoutIdRef.current;
      timeoutId && clearTimeout(timeoutId);
      timeoutIdRef.current = window.setTimeout(() => {
        dispatch(commonActions.setValue('isPageLoadingSlow', true));
      }, 300);
    });

    const onRouteChangeFinished = () => {
      const timeoutId = timeoutIdRef.current;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutIdRef.current = undefined;
        dispatch(commonActions.setValuesWhenRoutingFinished());
      }
    };
    router.events.on('routeChangeComplete', onRouteChangeFinished);
    router.events.on('routeChangeError', onRouteChangeFinished);
    router.beforePopState(processBeforePopState);
  }, [dispatch, router]);

  const userAgent = useUserAgent();
  useEffect(() => {
    if (userAgent?.isIos) {
      // 디바이스 회전과 관련한 처리를 위해 window에 이벤트리스너 추가함
      // onOrientationChange 함수의 주석 참고할 것
      window.addEventListener('orientationchange', onOrientationChanged);
    }

    // 파이어폭스에서 location.replace후 브라우저에서 뒤로가기 클릭시 url은 변경되지만 화면은 그대로인 문제가 있다.
    // 그래서 unload 이벤트가 발생하면 해당 url로 다시한번 reload 한다.
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1422334#c23
    if (userAgent?.isFirefox) {
      window.addEventListener('unload', () => {
        location.reload();
      });
    }
  }, [dispatch, userAgent]);

  useEffect(() => {
    dispatch(authActions.fetchUser({needDetail: true}));
  }, [dispatch]);

  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    addEventForScroll(router);
  }, [router]);

  useEffect(() => {
    initTracking();
    initGtm();
    createKakaowebtoonWeb();
  }, []);

  useIsomorphicLayoutEffect(() => {
    router.events.on('routeChangeComplete', checkAndSendPageView);
    checkAndSendPageView(); // 최초에는 routeChangeComplete 이벤트가 발생하지 않아서 직접 호출한다.
  }, [router]);

  useEffect(() => {
    if (IS_DEBUG && !IS_PROD) {
      addEventListener('touchstart', checkAndRouteToDebugLog);
    }
    dispatch(commonActions.setIsAppMounted(true));
  }, [dispatch]);

  useEffect(() => {
    // TODO : 작품홍 > 공지사항 개편 후 삭제
    if (currentPage === Page.WebviewNotice) {
      return;
    }

    document.body.classList.remove('bg-background', 'bg-background-02');
    document.body.classList.add(getBgCN(currentPage));
  }, [currentPage]);

  useAuthResult(router);
  useKakaoSdk();
  useRouteToContentObserver();
  useFetchWebToAppBanner();
  useResizeCssVars();
  const isLite = useIsLite(userAgent);

  const page = pageMap[currentPage];
  const pageContext: IPageContext = {
    title: context.page === currentPage ? context.title : '',
  };
  if (page && !isEqual(pageContext, page?.context)) {
    // TODO: 리액트 concurrent mode 를 위해 렌더링 중에 ref 값 수정하지 않기
    page.context = pageContext;
  }

  return (
    <ErrorBoundary>
      <ThemeProvider initialTheme={theme}>
        <LiteVersionContext.Provider value={isLite}>
          <I18nextProvider i18n={getI18nextInstance(lang, store)}>
            <PageAppContext.Provider value={memoizedContext}>
              <Meta />
              <StructuredData />
              <Layout isFullWidth={IS_FULL_WIDTH_PAGE_MAP[currentPage]}>
                {page && (
                  <PageContext.Provider key={currentPage} value={page.context}>
                    <page.Component {...page.pageProps} />
                  </PageContext.Provider>
                )}
              </Layout>
              <PAlert />
              <PToast />
            </PageAppContext.Provider>
          </I18nextProvider>
        </LiteVersionContext.Provider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};
const IS_FULL_WIDTH_PAGE_MAP: {[key in Page]?: boolean} = {
  [Page.Home]: true,
  [Page.Event]: true,
  [Page.Viewer]: true,
  [Page.Search]: true,
  [Page.ContentKeyword]: true,
  [Page.Dev]: true,
};

KwApp.getInitialProps = async appContext => {
  const {
    Component,
    ctx: {store, req, query},
    router,
  } = appContext;
  const userAgent: ReturnType<typeof getUserAgent> = IS_CLIENT
    ? store.getState().common.userAgent
    : getUserAgent(req!.headers['user-agent']);
  appContext.ctx.userAgent = userAgent;

  const lang: ILanguage =
    (req as any)?.pageParam.lang || store.getState().common.lang;
  const isNeedToInitializeStore = !store.getState().common.lang;
  if (isNeedToInitializeStore) {
    store.dispatch(commonActions.setValue('lang', lang));
    store.dispatch(commonActions.setValue('initRandom', Math.random()));
  }
  const theme: ITheme = (req as any)?.pageParam.theme;
  store.dispatch(commonActions.setValue('theme', theme));
  if (!IS_CLIENT) {
    store.dispatch(
      commonActions.setValue('headers', {
        cookie: req?.headers.cookie || '',
        'user-agent': req?.headers['user-agent'] || '',
      })
    );
  }
  const i18next = getI18nextInstance(lang, store);

  const {getPageTitle, ...layoutProps} = Component.initialPageProps;
  const {page} = layoutProps;

  const {pageProps} = await App.getInitialProps(appContext);
  if (Component.getInitialProps && !IS_CLIENT) {
    await waitSaga(store);
  }

  pushLog({msg: `getInitialProps page=/${page}`, params: pageProps});
  store.dispatch(
    commonActions.setValue('layoutProps', {
      ...layoutProps,
    })
  );

  const currentUrl = `${WEB_HOST}${router.asPath}`;
  // getPageTitle은 string을 반환해야하지만 그렇지 않은 경우가 있어서 sentry에 에러가 찍힌다. 방어 코드로 빈 문자열을 넣는다
  const title = getPageTitle(i18next);
  const rid = typeof query.rid === 'string' ? query.rid : '';
  const context: IPageAppContext = {
    page,
    prevPage: undefined,
    currentUrl,
    title,
    rid,
  };
  setAuthResult({store, query});

  const serverContentAdultMap = IS_CLIENT
    ? undefined
    : cloneDeep(contentAdultMap);
  !IS_CLIENT && initializeContentAdultMap();
  return {
    pageProps,
    context,
    lang,
    serverContentAdultMap,
    theme,
  };
};

async function onOrientationChanged() {
  // iPad 앱의 웹뷰 내에서 디바이스가 회전했을 때, resize 이벤트가 회전 시작하는 시점에 발생하므로
  // 그 순간의 window의 innerWidth와 innerHeight 값이 회전이 끝나는 시점의 값과 다름.
  // 회전이 끝나는 시점을 정확히 알 수가 없으므로, 적절한 시간 이후 다시 윈도우에 resize 이벤트를 발생시켜 해결함.
  // (iOS 사파리에서는 resize 시점이 문제가 없어보이는데, iOS 서드파티 앱 내에 사용되는 웹뷰에서는 동일한 문제가 있음.)
  // 참고: https://stackoverflow.com/questions/12452349/mobile-viewport-height-after-orientation-change
  await _onOrientationChanged();
  window.dispatchEvent(new Event('resize'));
}
function _onOrientationChanged() {
  const timeout = 100;
  return new Promise<void>(resolve => {
    const go = (
      i: number,
      baseSize: {innerWidth: number; innerHeight: number}
    ) => {
      (window.innerWidth !== baseSize.innerWidth &&
        window.innerHeight !== baseSize.innerHeight) ||
      i >= timeout
        ? resolve()
        : window.requestAnimationFrame(() => go(i + 1, baseSize));
    };
    const {innerWidth, innerHeight} = window;
    go(0, {innerWidth, innerHeight});
  });
}

let popState: {scrollY: number} | undefined;
function addEventForScroll(router: AppContext['router']) {
  const IGNORE_PAGE: string[] = [Page.Home];
  const NOT_RESTORE_SCROLL_PAGE: string[] = [];
  /*
  쿼리 파라미터가 있는 경우와 없는 경우가 같은 페이지를 가리키는 경우에 문제가 될 수 있다
  ex) /webtoon === /webtoon?tab=10
  routeBack으로 이동하는 경우에는 괜찮고, routeTo로 이동하는 경우에 문제가 된다
  이때는 해당 페이지에서 쿼리가 붙은 url로 replace 해줘야 한다
  */
  const customScrollInfo: {
    [path: string]: number;
  } = {};

  router.events.on('routeChangeStart', () => {
    const currentPage = getCurrentClientPage();
    if (NOT_RESTORE_SCROLL_PAGE.includes(currentPage)) {
      return;
    }

    const {pageYOffset} = window; // scrollY와 동일한 값이나 브라우저 호환성이 더 좋다.
    const {state: prevState} = window.history;

    if (IGNORE_PAGE.includes(currentPage)) {
      customScrollInfo[router.asPath] = pageYOffset;
    }
    window.history.replaceState(
      {...prevState, scroll: {scrollY: pageYOffset}},
      window.document.title
    );
  });

  window.onpopstate = (event: PopStateEvent) => {
    if (event.state) {
      popState = event.state.scroll;
    }
  };

  router.events.on('routeChangeComplete', () => {
    if (NOT_RESTORE_SCROLL_PAGE.includes(getCurrentClientPage())) {
      return;
    }

    const scrollY =
      customScrollInfo[router.asPath] || (popState ? popState.scrollY : 0);

    window.scrollTo(0, scrollY);
    popState = undefined;
  });
}

export default withRedux(KwApp);
